import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { tr } from "@/lib/i18n";
import { sfx } from "@/lib/audio";

const COLORS = ["#FFD700", "#E21B3C", "#1368CE", "#26890C", "#864CBF", "#00F0FF"];
const HEIGHTS = { 1: "22em", 2: "16em", 3: "11em" };
const BAR_BG = { 1: "#FFD700", 2: "#E5E7EB", 3: "#F4A460" };

const Confetti = () => {
  const bits = useMemo(() => Array.from({ length: 50 }, (_, i) => ({
    left: `${Math.random() * 100}%`, delay: `${Math.random() * 4}s`, dur: `${3 + Math.random() * 3}s`, color: COLORS[i % COLORS.length],
  })), []);
  return bits.map((b, i) => (
    <div key={i} className="confetti" style={{ left: b.left, background: b.color, animationDelay: b.delay, animationDuration: b.dur }} />
  ));
};

export const PodiumScene = ({ players, lang, onExit }) => {
  const top = players.slice(0, 3);
  // visual order: 2nd (left), 1st (center), 3rd (right)
  const podium = [
    top[1] && { p: top[1], place: 2 },
    top[0] && { p: top[0], place: 1 },
    top[2] && { p: top[2], place: 3 },
  ].filter(Boolean);
  // reveal sequence: 3rd, then 2nd, then winner
  const revealOrder = [3, 2, 1].filter((pl) => podium.some((x) => x.place === pl));

  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = revealOrder.map((place, i) => setTimeout(() => {
      setStep(i + 1);
      if (place === 1) sfx.fanfare(); else sfx.whoosh();
    }, 700 + i * 1500));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRevealed = (place) => {
    const i = revealOrder.indexOf(place);
    return i !== -1 && i < step;
  };
  const winnerRevealed = isRevealed(1);

  return (
    <div className="absolute inset-0" data-testid="host-podium-scene">
      <div className="store-bg" style={{ filter: "brightness(0.35)" }} />
      {winnerRevealed && <Confetti />}
      <div className="absolute left-0 right-0 top-[2.6em] flex flex-col items-center">
        <span className="font-press text-[2.2em] text-white" style={{ textShadow: "0.12em 0.12em 0 #000" }}>{tr(lang, "salesperson_of_day").toUpperCase()}</span>
        <AnimatePresence>
          {winnerRevealed && top[0] && (
            <motion.span initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}
              data-testid="host-winner-name" className="font-pixel font-bold text-[4.4em] text-[#FFD700] mt-[0.2em]" style={{ textShadow: "0.08em 0.08em 0 #000" }}>
              {top[0].name}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 left-[20em] right-[20em] flex items-end justify-center gap-[1.6em]">
        {podium.map(({ p, place }) => (
          <div key={p.id} className="flex flex-col items-center justify-end w-[17em]">
            <AnimatePresence>
              {isRevealed(place) && (
                <motion.div className="flex flex-col items-center w-full" initial={{ y: "34em", opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 120, damping: 15 }}>
                  <div className="font-pixel font-bold text-[2.1em] mt-[0.3em] max-w-full truncate" data-testid={`host-podium-name-${place}`}>{p.name}</div>
                  <div className="font-num text-[2.2em] text-[#FFD700] mb-[0.6em] leading-none">{p.score} {tr(lang, "pts")}</div>
                  <div className="px-box w-full flex justify-center pt-[0.6em]" style={{ height: HEIGHTS[place], background: BAR_BG[place] }}>
                    <span className="font-num text-[5em] leading-none">{place}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <button data-testid="host-back-dashboard-button" className="px-btn text-[1.1em] absolute right-[2em] top-[2em]" onClick={onExit}>
        {tr(lang, "back_dashboard")}
      </button>
    </div>
  );
};
